import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from "@/components/ui/Modal";

import { X } from "lucide-react";

import { Controller, useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import RHFInput from "@/components/form/RHFInput";
import RHFSelect from "@/components/form/RHFSelect";
import RHFCheckboxGroup from "@/components/form/RHFCheckboxGroup";
import RHFCheckbox from "@/components/form/RHFCheckbox";
import { currencyOptions } from "@/utils/currencyOptions";
// Sourcing/Extension is disabled — commented out, not deleted. See PLAN
// doc "resilient-stirring-tulip".
// import { EXTENSION_SITE_OPTIONS } from "@/config/extensionSites";
import { COUNTRY_MASTER_OPTIONS } from "@/config/countryMasters";

import { buildTenantFormSchema } from "../validation.schema";
import { countryMastersForCreate } from "../payload";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import Callout from "@/components/ui/Callout";
import { cn } from "@/lib/utils";

import { useCallback, useEffect, useMemo, useState } from "react";
import { getCountryList } from "@/utils/countryList";
import ModuleSelector from "./ModuleSelector";
// CV Formatting is disabled — commented out, not deleted. See PLAN doc
// "resilient-stirring-tulip".
// import TemplateAccessPicker from "./TemplateAccessPicker";
import Select from "@/components/ui/Select";
import toast from "react-hot-toast";
import { ModuleService } from "@/services/moduleService";
import { GuardianService } from "@/services/guardianService";
// import { ResumeTemplateAdminService } from "@/services/resumeTemplateAdminService";
import { GUARDIAN_POLICIES, GUARDIAN_POLICY_LABELS, GUARDIAN_SWITCHES, MODULES } from "@/permissions/modules";
import { warnOnRegistryDrift } from "@/permissions/moduleSurfaces";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

// Sourcing, Extension, Interview Scheduling and CV Formatting are disabled
// on the frontend — commented out, not deleted, across the app (see PLAN
// doc "resilient-stirring-tulip"). Filtered out here too, at the one place
// this modal loads a tenant's module list, so none of the three ever
// appears as a grantable option — in the picker below, in the review-step
// summary, or in the saved payload.
const HIDDEN_MODULE_CODES = [MODULES.SOURCING, MODULES.EXTENSION, MODULES.INTERVIEW_SCHEDULING, MODULES.RESUME_TEMPLATE];

// Why the country document sets lock when Compliance is unticked. Exported so
// the copy is asserted where the rule is tested, rather than retyped there.
//
// A country document set IS a compliance checklist: without the module, every
// /api/v2/compliance/* route answers 403 MODULE_NOT_ENABLED, so a seeded set is
// a row the tenant has no screen to open. Said out loud on the control because
// a box that greys out for no visible reason is the one a SuperAdmin files a
// bug about.
export const COUNTRY_MASTERS_NEED_COMPLIANCE = "Needs the Compliance module";

const POLICY_OPTIONS = [
  { value: GUARDIAN_POLICIES.TENANT_CHOICE, label: GUARDIAN_POLICY_LABELS[GUARDIAN_POLICIES.TENANT_CHOICE] },
  { value: GUARDIAN_POLICIES.FORCED_ON, label: GUARDIAN_POLICY_LABELS[GUARDIAN_POLICIES.FORCED_ON] },
  { value: GUARDIAN_POLICIES.FORCED_OFF, label: GUARDIAN_POLICY_LABELS[GUARDIAN_POLICIES.FORCED_OFF] },
];

// The two ways a tenant's people reach the product, named. `adLoginEnabled` is
// the boolean the server stores; the copy is what the SuperAdmin reads. The
// login page calls the AD form "Corporate Login" because that is the employee's
// word for it — here it is the directory's own name, matching the tenant-details
// row ("Active Directory Sign-in").
const SIGN_IN_METHODS = [
  {
    adLoginEnabled: false,
    label: "Email and Password",
    description: "People are invited by a tenant admin and sign in with their email address. Every account counts against the seat licence.",
  },
  {
    adLoginEnabled: true,
    label: "Active Directory",
    description:
      "People sign in with their employee ID against the corporate directory. Accounts appear on first sign-in, so this tenant's admins invite nobody and no seat licence applies.",
  },
];

const INPUT_STYLE = "w-full rounded-lg px-4 py-2.5 text-sm placeholder:text-text-muted bg-background-form/40 focus:outline-none transition text-text-primary";

const DOMAIN_BASE = import.meta.env.VITE_DOMAIN_BASE;

const AddEditModel = ({ isOpen, onModelClose, activeModal = "add", onHandleSubmit, defaultValues, tenantId, existingAdmin }) => {
  // React Hook Form setup with validation schema. On edit the tenant's own
  // slug is grandfathered in — see buildTenantFormSchema.
  const schema = useMemo(
    () =>
      buildTenantFormSchema(activeModal === "edit" ? (defaultValues?.slug ?? "") : "", {
        // Only Add creates the tenant's first admin. On Edit the tenant already
        // has its admins, they are managed on its Users screen, and the update
        // route carries no admin field — so Edit renders none of the three.
        requireFirstAdmin: activeModal !== "edit",
      }),
    [activeModal, defaultValues?.slug],
  );

  const methods = useForm({
    resolver: zodResolver(schema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    watch,
    setValue,
  } = methods;

  const isEdit = activeModal === "edit";

  // ── Module entitlements ───────────────────────────────────────────────
  // Both Add and Edit. The tenant-details Modules tab is where a plan is
  // *read*; this modal is where it is *changed*, behind the Edit button
  // where a SuperAdmin looks for it. Fleet-wide availability travels with
  // each row either way, so nothing is edited blind.
  //
  // On Add the rows come from the registry; on Edit they come from the
  // tenant's own entitlements, which carry `stored` and `effective`.
  const [platformModules, setPlatformModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [selectedModules, setSelectedModules] = useState(() => new Set());

  // ── CV template grants ─────────────────────────────────────────────────
  // A second, independent axis nested under the CV Formatting module row:
  // the module says "does this tenant have CV formatting at all", this says
  // "which of the 17 designs". `storedTemplateKeys` is what Edit loaded, so
  // save can skip the PUT when nothing actually changed — renaming a tenant
  // must not rewrite every grant row.
  const [templateOptions, setTemplateOptions] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState(() => new Set());
  const [storedTemplateKeys, setStoredTemplateKeys] = useState(() => new Set());

  // ── Prompt-safety policy ──────────────────────────────────────────────
  // Add and Edit, and it reads the same on both.
  //
  // It is a per-tenant override of a platform default rather than part of
  // the plan, but it is still a decision a SuperAdmin makes *about* a
  // tenant, so it belongs beside every other one. It used to be the single
  // editable control on the read-only View screen.
  //
  // On Add the platform policy is still what decides whether the control is
  // live: a fleet-wide FORCED_ON/FORCED_OFF cannot be overridden for one
  // tenant, so the control is shown locked with the reason rather than
  // hidden — a SuperAdmin creating a tenant should see what the tenant will
  // get. With "Tenant decides" in place the override can be set here, and it
  // is written straight after the tenant exists.
  const [guardianPolicy, setGuardianPolicy] = useState(GUARDIAN_POLICIES.TENANT_CHOICE);
  const [storedGuardianPolicy, setStoredGuardianPolicy] = useState(GUARDIAN_POLICIES.TENANT_CHOICE);
  const [platformGuardianPolicy, setPlatformGuardianPolicy] = useState(GUARDIAN_POLICIES.TENANT_CHOICE);
  const [guardianAvailable, setGuardianAvailable] = useState(true);
  const [guardianLoaded, setGuardianLoaded] = useState(false);
  // Two-step create: fill the form, then review the module selection before
  // anything is written. A mistake at creation is the one nobody notices.
  const [reviewing, setReviewing] = useState(false);
  const [pendingForm, setPendingForm] = useState(null);
  // Create is a long request — a dedicated DB takes 15–30s — and it is not
  // driven by `handleSubmit`, so react-hook-form's `isSubmitting` stays
  // false throughout it. Without this the Confirm button stayed live for the
  // whole provisioning run, and a second click created the tenant twice: the
  // first call won, the rest came back "slug already exists".
  const [creating, setCreating] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (!isOpen) return;
    reset(defaultValues);
    setReviewing(false);
    setPendingForm(null);
    setCreating(false);
  }, [isOpen, defaultValues, reset]);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit && !tenantId) return;
    let cancelled = false;
    const load = async () => {
      try {
        setModulesLoading(true);

        if (isEdit) {
          // The tenant's own rows. `stored` is what they bought —
          // a child of a switched-off parent keeps its tick, so
          // switching the parent back on restores it untouched.
          const data = await ModuleService.getTenantModules(tenantId);
          if (cancelled) return;
          const list = (data?.modules ?? []).filter((m) => !HIDDEN_MODULE_CODES.includes(m.code));
          setPlatformModules(list);
          setSelectedModules(new Set(list.filter((m) => m.stored).map((m) => m.code)));
          return;
        }

        // Add: the sellable catalogue, decorated with each module's
        // current fleet-wide availability so granting something dark
        // carries its warning.
        const [registry, platform] = await Promise.all([ModuleService.getModuleRegistry(), ModuleService.getPlatformModules()]);
        if (cancelled) return;
        // Development only, and only here — this page already has the
        // catalogue in hand, so the check costs no request.
        warnOnRegistryDrift(registry?.modules);
        const availability = new Map((platform?.modules ?? []).map((m) => [m.code, m.available]));
        const list = (registry?.modules ?? [])
          .filter((m) => m.sellable && !HIDDEN_MODULE_CODES.includes(m.code))
          .map((m) => ({ ...m, available: availability.get(m.code) !== false }));
        setPlatformModules(list);
        // Pre-fill with every sellable module. Matches the backend's
        // DEFAULT_MODULE_CODES; the SuperAdmin narrows it from here.
        setSelectedModules(new Set(list.map((m) => m.code)));
      } catch {
        // Non-fatal: leaving the list empty makes the selector say so,
        // and omitting `modules` from the payload falls back to the
        // platform defaults server-side.
        if (!cancelled) setPlatformModules([]);
      } finally {
        if (!cancelled) setModulesLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, isEdit, tenantId]);

  // CV Formatting is disabled — commented out, not deleted. See PLAN doc
  // "resilient-stirring-tulip".
  //
  // // CV template grants — mirrors the module load above, but on its own
  // // endpoint. Edit reads this tenant's `granted` flags; Add has no tenant
  // // yet, so it reads the catalogue and preselects nothing — several
  // // templates are other clients' brands (Mediteam, Medpure, Dedicare,
  // // Elizabeth), so a new tenant must never inherit them by default.
  // useEffect(() => {
  //   if (!isOpen) return;
  //   if (isEdit && !tenantId) return;
  //   let cancelled = false;
  //   const load = async () => {
  //     try {
  //       setTemplatesLoading(true);
  //
  //       if (isEdit) {
  //         const res = await ResumeTemplateAdminService.fetchTenantTemplates(tenantId);
  //         if (cancelled) return;
  //         const list = res?.data ?? [];
  //         setTemplateOptions(list.map((t) => ({ key: t.key, name: t.name, description: t.description })));
  //         const granted = new Set(list.filter((t) => t.granted).map((t) => t.key));
  //         setSelectedTemplates(granted);
  //         setStoredTemplateKeys(granted);
  //         return;
  //       }
  //
  //       const catalog = await ResumeTemplateAdminService.fetchCatalog();
  //       if (cancelled) return;
  //       const list = catalog?.data ?? [];
  //       setTemplateOptions(list.map((t) => ({ key: t.key, name: t.name, description: t.description })));
  //       setSelectedTemplates(new Set());
  //       setStoredTemplateKeys(new Set());
  //     } catch {
  //       // Non-fatal: an empty catalogue makes the picker say so, and
  //       // no `resumeTemplateKeys` is sent, so create/edit still go
  //       // through without touching template grants.
  //       if (!cancelled) setTemplateOptions([]);
  //     } finally {
  //       if (!cancelled) setTemplatesLoading(false);
  //     }
  //   };
  //   load();
  //   return () => {
  //     cancelled = true;
  //   };
  // }, [isOpen, isEdit, tenantId]);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit && !tenantId) return;
    let cancelled = false;
    setGuardianLoaded(false);
    // Edit reads the tenant's own override *and* the platform default it
    // sits under. Add has no tenant yet, so the platform endpoint answers
    // with the platform policy alone, and the new tenant starts with no
    // override of it.
    const request = isEdit ? GuardianService.getTenantPolicy(tenantId) : GuardianService.getPlatformPolicy();
    request
      .then((data) => {
        if (cancelled) return;
        const stored = isEdit ? data?.policies?.[GUARDIAN_SWITCHES.MASTER] || GUARDIAN_POLICIES.TENANT_CHOICE : GUARDIAN_POLICIES.TENANT_CHOICE;
        const platform = isEdit ? data?.platformPolicies?.[GUARDIAN_SWITCHES.MASTER] : data?.policies?.[GUARDIAN_SWITCHES.MASTER];
        setStoredGuardianPolicy(stored);
        setGuardianPolicy(stored);
        setPlatformGuardianPolicy(platform || GUARDIAN_POLICIES.TENANT_CHOICE);
        setGuardianAvailable(data?.availableOnDeployment !== false);
        setGuardianLoaded(true);
      })
      .catch(() => {
        // Leave the control out rather than show a default that is
        // probably a lie. Saving still works for everything else.
        if (!cancelled) setGuardianLoaded(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, isEdit, tenantId]);

  // A fleet-wide FORCED_ON/FORCED_OFF decides this switch for every tenant, so
  // there is nothing to set here while one is in place. The server refuses the
  // write too — see `module_config_service.set_guardian_policies`.
  const platformForces = platformGuardianPolicy !== GUARDIAN_POLICIES.TENANT_CHOICE;

  const sellableModules = platformModules;

  // Compliance is the parent of the country document sets: a set is a
  // compliance checklist, and without the module the tenant has no screen that
  // opens one. Read from the live tick rather than the saved plan so the
  // country control answers the selection being made right now.
  const complianceSelected = selectedModules.has(MODULES.COMPLIANCE);

  // The ticked countries as the SuperAdmin named them, for the review step.
  // `complianceCountryMasters` holds ISO alpha-2 codes — the thing the API
  // stores and the seeder keys on — and "GB, IN" is not what anyone ticked.
  // Mapped through the same option list the checkboxes render from, so a
  // country added there needs no second edit here.
  //
  // Read off `pendingForm` rather than `watch()`: the review step confirms the
  // snapshot taken when the form was submitted, and the form is unmounted
  // behind it.
  const reviewCountryLabels = useMemo(() => {
    const picked = pendingForm?.complianceCountryMasters || [];
    return COUNTRY_MASTER_OPTIONS.filter((o) => picked.includes(o.value)).map((o) => o.label);
  }, [pendingForm]);

  const handleModuleToggle = useCallback(
    (code) => {
      setSelectedModules((current) => {
        if (code === "__all__") {
          return new Set(sellableModules.map((m) => m.code));
        }
        if (code === "__none__") return new Set();
        const next = new Set(current);
        if (next.has(code)) {
          next.delete(code);
          // Unticking a parent takes its children with it — a locked
          // child left ticked would still submit as granted, handing the
          // tenant a module the SuperAdmin just turned off. Re-ticking the
          // parent unlocks the children but does not restore them; the
          // SuperAdmin picks them again.
          sellableModules.filter((m) => m.parent === code).forEach((m) => next.delete(m.code));
        } else {
          next.add(code);
        }
        return next;
      });
      // Deliberately does not touch `selectedTemplates`. Unticking CV
      // Formatting only greys its picker (see TemplateAccessPicker's
      // `locked`) — the grants themselves are untouched and still
      // submit unchanged, so re-ticking the module restores exactly
      // what was there.
    },
    [sellableModules],
  );

  // CV Formatting is disabled — commented out, not deleted.
  // const handleTemplateToggle = useCallback(
  //   (key) => {
  //     setSelectedTemplates((current) => {
  //       if (key === "__all__") {
  //         return new Set(templateOptions.map((t) => t.key));
  //       }
  //       if (key === "__none__") return new Set();
  //       const next = new Set(current);
  //       if (next.has(key)) next.delete(key);
  //       else next.add(key);
  //       return next;
  //     });
  //   },
  //   [templateOptions],
  // );

  // Outreach has no product with zero channels to send through — a tenant
  // saved with Outreach checked and both channel siblings off hits a dead
  // end where every Templates/Sequences create action is blocked. Checked
  // once, here, ahead of both the Edit path and the Create review step —
  // the review screen shows the module list read-only, so there is nowhere
  // later to catch this.
  const validateOutreachChannels = () => {
    if (!selectedModules.has(MODULES.OUTREACH)) return true;
    if (selectedModules.has(MODULES.OUTREACH_EMAIL) || selectedModules.has(MODULES.OUTREACH_CHAT)) return true;
    toast.error("Outreach requires at least one channel — Email or Chat Channels — to be enabled.");
    return false;
  };

  const onValidSubmit = async (formData) => {
    if (!validateOutreachChannels()) return undefined;
    if (isEdit) {
      // CV Formatting is disabled — commented out, not deleted.
      //
      // // CV template grants — its own endpoint, saved first and awaited.
      // // Skipped when the ticks match what was loaded, so renaming a
      // // tenant does not rewrite every grant row. On failure, stop here
      // // entirely: nothing else is saved, so a template failure can
      // // never masquerade as a successful tenant update.
      // const templatesChanged = selectedTemplates.size !== storedTemplateKeys.size || [...selectedTemplates].some((key) => !storedTemplateKeys.has(key));
      // if (templatesChanged) {
      //   try {
      //     await ResumeTemplateAdminService.setTenantTemplates(tenantId, Array.from(selectedTemplates));
      //     setStoredTemplateKeys(new Set(selectedTemplates));
      //   } catch (error) {
      //     toast.error(apiErrorMessageFormat(error, "Failed to update CV template access. Nothing was saved."));
      //     return undefined;
      //   }
      // }

      // The policy has its own endpoint, so it is saved separately.
      // First, and awaited: if it fails, nothing else is written and the
      // SuperAdmin is told, rather than being shown a successful save
      // that quietly dropped half of what they changed.
      // `!platformForces` is belt-and-braces: the control is locked in that
      // state, so this can only differ if the platform policy changed in
      // another tab. The server refuses it either way; not sending it means
      // the rest of the form still saves.
      if (guardianLoaded && !platformForces && guardianPolicy !== storedGuardianPolicy) {
        try {
          await GuardianService.updateTenantPolicy(tenantId, {
            [GUARDIAN_SWITCHES.MASTER]: guardianPolicy,
          });
          setStoredGuardianPolicy(guardianPolicy);
        } catch (error) {
          toast.error(apiErrorMessageFormat(error, "Failed to update the prompt-safety policy. Nothing was saved."));
          return undefined;
        }
      }

      // A full map, not just what changed. Every sellable code sends
      // its current tick — including a child locked by its parent,
      // which sends the value it already had. Sending `false` because
      // a control happened to be disabled would erase the entitlement.
      const moduleMap = Object.fromEntries(sellableModules.map((m) => [m.code, selectedModules.has(m.code)]));
      return onHandleSubmit({
        ...formData,
        ...(sellableModules.length ? { modules: moduleMap } : {}),
      });
    }
    // Hold the form and show the review step instead of creating straight
    // away.
    setPendingForm(formData);
    setReviewing(true);
    return undefined;
  };

  const confirmCreate = async () => {
    // Second guard behind the disabled button: a keyboard Enter repeat or
    // a double-click landing in the same tick must not start two creates.
    if (!pendingForm || creating) return;
    setCreating(true);
    try {
      await onHandleSubmit({
        ...pendingForm,
        // Only send the list when we actually have a registry to select
        // from. Omitting it lets the server apply its own defaults rather
        // than us silently creating a tenant with nothing enabled.
        ...(platformModules.length ? { modules: Array.from(selectedModules) } : {}),
        // The country sets follow the module. The ticks survive the lock, so
        // this is where "ticked, but Compliance is off" is resolved — to none,
        // rather than to checklists the tenant has no screen to open. See
        // countryMastersForCreate.
        complianceCountryMasters: countryMastersForCreate(pendingForm.complianceCountryMasters, complianceSelected),
        // CV template grants need a tenant id that does not exist
        // yet, so the caller (tenant/index.jsx) PUTs them after
        // create — mirrors the guardianPolicy deferral just below.
        // Omitted when the catalogue never loaded, same reasoning as
        // `modules` above.
        // CV Formatting is disabled — commented out, not deleted.
        // ...(templateOptions.length ? { resumeTemplateKeys: Array.from(selectedTemplates) } : {}),
        // The policy has its own endpoint and needs a tenant id, so it
        // is written by the caller right after the tenant exists. Sent
        // only when it is an actual override the platform allows —
        // "Tenant decides" is the absence of an override, so writing it
        // would be a no-op request.
        ...(guardianLoaded && !platformForces && guardianPolicy !== GUARDIAN_POLICIES.TENANT_CHOICE ? { guardianPolicy } : {}),
      });
    } finally {
      setCreating(false);
    }
  };

  // slug value
  const slug = watch("slug");

  // Which sign-in method is picked. Everything about the first admin follows
  // from it: an email tenant's is invited by address, an AD tenant's is named
  // by corporate ID and no invitation exists to send.
  const adLoginEnabled = !!watch("adLoginEnabled");

  // DB mode radio value — hides the info panel when 'shared'.
  // Only meaningful on Add; the field is disabled on Edit because
  // switching a live tenant between modes is a Phase 2 operation.
  const dbMode = watch("dbMode") || "shared";

  // Dynamic submit button label — dedicated mode communicates the
  // provisioning step so SuperAdmin knows why the request may take
  // 15-30s.
  const submitLabel = isEdit ? "Update Tenant" : reviewing ? "Add Tenant" : dbMode === "dedicated" ? "Create & Provision Dedicated DB" : "Review Details";

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(isOpen) => {
        // Close modal when dialog requests close. Held shut while a
        // create is in flight — closing here does not cancel the
        // request, it only hides the one thing telling the SuperAdmin
        // that a tenant is being built.
        if (!isOpen) {
          if (creating) return;
          onModelClose();
        }
      }}
    >
      <DialogContent className="w-[340px] md:w-full flex flex-col">
        {/* ------------------------------------------------------------
                    Review step. Repeats the module selection — including the
                    "switched off fleet-wide" warnings — before the tenant is
                    created. Rendered instead of the form, not on top of it, so
                    there is no doubt about which state is being confirmed.
                ------------------------------------------------------------ */}
        {reviewing && pendingForm ? (
          <>
            <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
              <div>
                <DialogTitle className="text-[15px] font-semibold text-text-primary">Confirm new tenant</DialogTitle>
                <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5">Review what {pendingForm.name} will be created with.</DialogDescription>
              </div>
              <DialogClose
                disabled={creating}
                className="absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X size={16} strokeWidth={3} />
              </DialogClose>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-4 flex flex-col gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[13px]">
                <div>
                  <div className="text-text-primary/60 text-[11px] uppercase tracking-wide">Tenant</div>
                  <div className="text-text-primary">{pendingForm.name}</div>
                </div>
                <div>
                  <div className="text-text-primary/60 text-[11px] uppercase tracking-wide">Slug</div>
                  <div className="text-text-primary font-mono">{pendingForm.slug}</div>
                </div>
                {/* The answer every field above is read through, so it is
                    confirmed with them rather than assumed. */}
                <div>
                  <div className="text-text-primary/60 text-[11px] uppercase tracking-wide">Sign-in method</div>
                  <div className="text-text-primary">{SIGN_IN_METHODS.find((m) => m.adLoginEnabled === !!pendingForm.adLoginEnabled)?.label}</div>
                </div>
                <div>
                  {/* Whichever identity the tenant's sign-in method actually
                      uses, named. Showing an email for an AD tenant named an
                      account that was never going to exist, and the value alone
                      does not say which of the two it is. */}
                  <div className="text-text-primary/60 text-[11px] uppercase tracking-wide">
                    {pendingForm.adLoginEnabled ? "First admin — corporate ID" : "First admin — email"}
                  </div>
                  <div className="text-text-primary">{pendingForm.adLoginEnabled ? pendingForm.adminEmployeeId : pendingForm.adminEmail}</div>
                </div>
                <div>
                  <div className="text-text-primary/60 text-[11px] uppercase tracking-wide">Max Users</div>
                  {/* Spelled out rather than omitted: a missing row does not
                      say whether the limit is unset or simply not a limit. */}
                  <div className="text-text-primary">
                    {pendingForm.adLoginEnabled ? (
                      <span className="text-text-primary/60">Does not apply — the directory creates the accounts</span>
                    ) : (
                      `${pendingForm.maxUsers} users`
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-text-primary/60 text-[11px] uppercase tracking-wide">Database</div>
                  <div className="text-text-primary capitalize">{pendingForm.dbMode || "shared"}</div>
                </div>
                {guardianLoaded && (
                  <div className="sm:col-span-2">
                    <div className="text-text-primary/60 text-[11px] uppercase tracking-wide">Prompt-safety policy</div>
                    <div className="text-text-primary">
                      {GUARDIAN_POLICY_LABELS[platformForces ? platformGuardianPolicy : guardianPolicy]}
                      <span className="text-text-primary/60">
                        {" "}
                        {platformForces
                          ? "(set for every tenant on Platform Settings)"
                          : guardianPolicy === GUARDIAN_POLICIES.TENANT_CHOICE
                            ? "(follows the platform policy)"
                            : "(override for this tenant only)"}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <div className="text-text-primary/60 text-[11px] uppercase tracking-wide mb-1.5">
                  Modules ({selectedModules.size} of {sellableModules.length})
                </div>
                {selectedModules.size === 0 ? (
                  <Callout size="sm" className="px-3 py-2">
                    No modules selected. This tenant will be created with no optional features at all — they will see only the always-on core (jobs, candidates, users,
                    roles).
                  </Callout>
                ) : (
                  <ul className="flex flex-col gap-1.5">
                    {sellableModules
                      .filter((m) => selectedModules.has(m.code))
                      .map((m) => (
                        <li key={m.code} className="text-[13px] text-text-primary">
                          <div className="flex items-baseline gap-2">
                            <span>{m.name}</span>
                            {m.available === false && <span className="text-[11px] text-tone-amber">(switched off fleet-wide right now)</span>}
                          </div>
                          {/* "CV Formatting: on" alone does not tell the SuperAdmin
                                                        whether recruiters will find anything to export. */}
                          {m.code === MODULES.RESUME_TEMPLATE &&
                            (selectedTemplates.size > 0 ? (
                              <p className="mt-0.5 text-[11px] text-text-primary/60">
                                {selectedTemplates.size} template{selectedTemplates.size === 1 ? "" : "s"}:{" "}
                                {templateOptions
                                  .filter((t) => selectedTemplates.has(t.key))
                                  .map((t) => t.name)
                                  .join(", ")}
                              </p>
                            ) : (
                              <p className="mt-0.5 text-[11px] text-tone-amber">No templates selected — recruiters will see an empty list.</p>
                            ))}
                          {/* Same reasoning as the templates line above:
                              "Compliance: on" does not tell the SuperAdmin
                              which country document sets the tenant is about
                              to be seeded with, and a seeded set is the one
                              thing on this screen that writes rows rather than
                              flipping a flag. Named individually — a count
                              alone invites them to go back and check which. */}
                          {m.code === MODULES.COMPLIANCE &&
                            (reviewCountryLabels.length > 0 ? (
                              <p className="mt-0.5 text-[11px] text-text-primary/60">
                                {reviewCountryLabels.length} country document set{reviewCountryLabels.length === 1 ? "" : "s"}: {reviewCountryLabels.join(", ")}
                              </p>
                            ) : (
                              // Spelled out rather than omitted: a missing line
                              // does not say whether none were picked or the
                              // row simply does not apply.
                              <p className="mt-0.5 text-[11px] text-text-primary/60">No country document sets — their team builds every checklist from scratch.</p>
                            ))}
                        </li>
                      ))}
                  </ul>
                )}
              </div>

              {sellableModules.some((m) => !selectedModules.has(m.code)) && (
                <div>
                  <div className="text-text-primary/60 text-[11px] uppercase tracking-wide mb-1.5">Not included</div>
                  <p className="text-[12px] text-text-primary/60">
                    {sellableModules
                      .filter((m) => !selectedModules.has(m.code))
                      .map((m) => m.name)
                      .join(", ")}
                  </p>
                </div>
              )}

              {/* Ticked countries with Compliance off. The ticks are kept on
                  purpose — greying the control never rewrites the state behind
                  it — so this is the only screen that can tell the SuperAdmin
                  those ticks will not become anything. Silence here would let
                  them confirm a creation they believe includes six country
                  sets. */}
              {!complianceSelected && reviewCountryLabels.length > 0 && (
                <Callout size="sm" className="px-3 py-2">
                  {reviewCountryLabels.join(", ")} {reviewCountryLabels.length === 1 ? "is" : "are"} ticked, but Compliance is not included — so no country document set
                  will be seeded. Go back and tick Compliance to include them.
                </Callout>
              )}

              <div className="flex justify-end gap-3 mt-2">
                <Button
                  variant="outline"
                  className="bg-button-tertiary border-none"
                  type="button"
                  onClick={() => setReviewing(false)}
                  disabled={isSubmitting || creating}
                >
                  Back
                </Button>
                <Button className="bg-button-secondary text-white" type="button" onClick={confirmCreate} disabled={isSubmitting || creating}>
                  {creating ? "Creating…" : submitLabel}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Header */}
            <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
              <div>
                <DialogTitle className="text-[15px] font-semibold text-text-primary">{activeModal === "edit" ? "Edit Tenant" : "Create Tenant"}</DialogTitle>

                <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5">
                  {activeModal === "edit" ? "Update the tenant details" : "Enter tenant details, then review before creating"}
                </DialogDescription>
              </div>

              {/* Close button */}
              <DialogClose className="absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center">
                <X size={16} strokeWidth={3} />
              </DialogClose>
            </DialogHeader>

            {/* Form body */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onValidSubmit)}>
                  <div className="px-5 py-3">
                    {/* Form fields grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <RHFInput
                          name="name"
                          label="Tenant Name"
                          placeholder="e.g. ACME"
                          inputClass={INPUT_STYLE}
                          maxLength={150}
                          maxLengthMessage="Tenant name must not exceed 150 characters"
                          required
                        />
                      </div>

                      {/* --------------------------------------
                                        Sign-in method — the single answer the
                                        whole product derives from: which form
                                        the login page offers, whether this
                                        tenant's admins may invite anyone, and
                                        whether an unknown employee ID
                                        provisions an account.

                                        Asked here, second only to the tenant's
                                        name, because every field below it reads
                                        differently once it is answered. It used
                                        to be a checkbox at the very bottom of
                                        the form, after the module tree, which
                                        is where nobody looks for a choice.

                                        Two named options rather than one box:
                                        an unticked box says nothing about what
                                        the tenant gets instead, and the pair
                                        follows the Database Mode cards further
                                        down this same form.

                                        Asked on Add and locked on Edit, the
                                        way Database Mode below it is: the
                                        accounts on the far side of the switch
                                        are the ones a change would strand. An
                                        AD account holds no password, so
                                        clearing the flag locks its holder out
                                        rather than moving them onto email;
                                        turning it on shuts every invite screen
                                        the tenant's admins use. The update
                                        route carries no such field, so the
                                        lock is the whole rule and not a
                                        client-side courtesy.
                                    -------------------------------------- */}
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Sign-in Method
                          {!isEdit && <span className="text-red-500 ml-0.5">*</span>}
                        </label>
                        <Controller
                          name="adLoginEnabled"
                          control={methods.control}
                          render={({ field }) => (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {SIGN_IN_METHODS.map((method) => {
                                const selected = !!field.value === method.adLoginEnabled;
                                return (
                                  <label
                                    key={method.label}
                                    className={cn(
                                      "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition",
                                      selected ? "border-button-secondary bg-button-secondary/5" : "border-border-primary",
                                      isEdit && "opacity-60 cursor-not-allowed",
                                    )}
                                  >
                                    <Input
                                      type="radio"
                                      name={field.name}
                                      checked={selected}
                                      disabled={isEdit}
                                      onChange={() => {
                                        field.onChange(method.adLoginEnabled);
                                        // Empty whatever the other method owns.
                                        // An unmounted field keeps its value in
                                        // form state, so a name typed before the
                                        // switch would ride into a payload the
                                        // route refuses — and an address left
                                        // behind reads as an admin who will be
                                        // mailed, when nothing will be.
                                        if (method.adLoginEnabled) {
                                          setValue("adminName", "", { shouldValidate: true });
                                          setValue("adminEmail", "", { shouldValidate: true });
                                          setValue("maxUsers", "", { shouldValidate: true });
                                        } else {
                                          setValue("adminEmployeeId", "", { shouldValidate: true });
                                          // Back to a form where it is required,
                                          // so it comes back as it was rather
                                          // than blank — nothing was corrected
                                          // by switching away and back.
                                          setValue("maxUsers", defaultValues?.maxUsers ?? "", { shouldValidate: true });
                                        }
                                      }}
                                      onBlur={field.onBlur}
                                      // A radio keeps the browser's own control, so the shared
                                      // Input's text-field box is dropped here.
                                      className="mt-1 w-auto appearance-auto rounded-none border-0 bg-transparent p-0"
                                    />
                                    <div>
                                      <div className="text-sm font-medium text-text-primary">{method.label}</div>
                                      <div className="text-xs text-text-primary/60 mt-0.5">{method.description}</div>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        />
                        {isEdit && (
                          <p className="text-xs text-text-primary/50 mt-2">
                            Fixed at creation. Changing how an organisation signs in would strand the accounts already on the far side of it, so a tenant that needs the
                            other method is created as a new tenant.
                          </p>
                        )}
                      </div>

                      <div>
                        <RHFInput
                          name="slug"
                          label="Slug"
                          placeholder="e.g. acme"
                          inputClass={INPUT_STYLE}
                          required
                          maxLength={63}
                          maxLengthMessage="Slug must not exceed 63 characters"
                          onChange={(e) => {
                            // Spaces and punctuation are dropped as you
                            // type. Digits are deliberately NOT dropped:
                            // they are invalid, and letting the schema
                            // say "Slug must not contain numbers" tells
                            // the SuperAdmin why, where a silent strip
                            // would look like a broken keyboard.
                            const sanitized = e.target.value
                              .toLowerCase()
                              .replace(/\s+/g, "")
                              .replace(/[^a-z0-9-]/g, "");

                            setValue("slug", sanitized, {
                              shouldValidate: true,
                              shouldDirty: true,
                              shouldTouch: true,
                            });
                          }}
                          onKeyDown={(e) => {
                            if (e.key === " ") e.preventDefault();
                          }}
                        />

                        <div className="mt-1 min-w-0">
                          <div className="text-xs text-text-primary/50 mb-1">
                            <span className="font-medium">Domain:</span>
                          </div>

                          {/* Theme tokens, not bg-gray-100 /
                                                text-gray-800: a fixed light
                                                grey chip was the brightest
                                                thing in the dark modal, and it
                                                is only a preview. */}
                          <div
                            className="max-w-full min-w-0 overflow-hidden rounded-md border border-border-primary bg-background-form/40 px-2 py-1 text-xs text-text-secondary font-mono whitespace-nowrap text-ellipsis"
                            title={`${slug || "your-slug"}.${DOMAIN_BASE}`}
                          >
                            {slug || "your-slug"}.{DOMAIN_BASE}
                          </div>
                        </div>
                      </div>

                      <RHFSelect
                        name="region"
                        label="Country"
                        placeholder="Select country"
                        options={getCountryList()}
                        searchable
                        searchPlaceholder="Search country..."
                        required
                      />

                      <RHFSelect
                        name="currency"
                        label="Currency"
                        placeholder="Select currency"
                        options={currencyOptions}
                        searchable
                        searchPlaceholder="Search currency..."
                        required
                        disabled={activeModal === "edit"}
                      />

                      {/* The seat licence, asked for where it is one. Every
                          invite route is shut for an AD tenant, so the number
                          caps nothing there — and the sign-in method is fixed
                          at creation, so it never starts to. */}
                      {!adLoginEnabled && <RHFInput name="maxUsers" label="Max Users" placeholder="e.g. 50" type="text" inputClass={INPUT_STYLE} required />}

                      {/* --------------------------------------
                                        maxCandidateStatusFlows caps how many
                                        named flows (Default, Junior Developer,
                                        Senior Developer, ...) the tenant may
                                        define at all. The per-flow status cap
                                        (how many steps ONE flow may hold) is
                                        fixed platform-wide at 15 and is not
                                        SuperAdmin-configurable — there is no
                                        field for it here.
                                    -------------------------------------- */}
                      <RHFInput
                        name="maxCandidateStatusFlows"
                        label="Max Status Flows"
                        placeholder="e.g. 10"
                        type="text"
                        inputClass={INPUT_STYLE}
                        required
                      />

                      {/* --------------------------------------
                                        The first admin.

                                        An email tenant's is invited: the two
                                        fields below are their name and the
                                        address the invitation goes to.

                                        An AD tenant's cannot be invited — the
                                        invite routes are shut for AD tenants,
                                        and a directory sign-in lands on the
                                        default-assignment role (Recruiter),
                                        which holds neither `role.manage` nor
                                        `user.view`. So the SuperAdmin names one
                                        employee by corporate ID, and that
                                        account arrives holding Admin on its
                                        first sign-in.

                                        Which means Add asks for one shape or
                                        the other and never both: the two
                                        invitation fields are not rendered at
                                        all for an AD tenant, rather than
                                        sitting there disabled asking for an
                                        address nothing would ever mail. The
                                        route refuses the same pair, so a form
                                        that asked for both would be a 422.

                                        Edit asks for none of them. A tenant's
                                        admins are managed on its Users screen
                                        and the update route carries no admin
                                        field, so this reads the one it has.
                                    -------------------------------------- */}
                      {isEdit ? (
                        // Read, not written. A tenant's admins are managed on
                        // the Users screen and the update route carries no
                        // admin field at all, so two boxes here only looked
                        // like they saved something — and on an AD tenant they
                        // read "Admin Name: E10432", the corporate ID standing
                        // in for a name until the directory fills it in.
                        <div className="sm:col-span-2">
                          {/* Not a <label>: there is no control under it to
                              name, only the identity the tenant already has. */}
                          <div className="block text-sm text-text-secondary mb-1.5">First admin</div>
                          <div className="rounded-md border border-border-input bg-background-form/40 px-4 py-2.5 text-sm text-text-primary">
                            {existingAdmin?.name ? (
                              <>
                                {existingAdmin.name}
                                <span className="text-text-primary/60">
                                  {" · "}
                                  {existingAdmin.email || (adLoginEnabled ? "signs in through Active Directory" : "no address on record")}
                                </span>
                              </>
                            ) : (
                              <span className="text-text-primary/60">No admin on this tenant</span>
                            )}
                          </div>
                          <p className="text-xs text-text-primary/50 mt-1">Admins are added and removed on this tenant's Users screen, not here.</p>
                        </div>
                      ) : adLoginEnabled ? (
                        <RHFInput
                          name="adminEmployeeId"
                          label="First Admin — Corporate ID"
                          placeholder="e.g. E10432"
                          inputClass={INPUT_STYLE}
                          required
                          className="sm:col-span-2"
                        />
                      ) : (
                        <>
                          <RHFInput name="adminName" label="Admin Name" placeholder="e.g. John Doe" inputClass={INPUT_STYLE} required />

                          <RHFInput name="adminEmail" label="Admin Email" placeholder="e.g. admin@acme.com" type="email" inputClass={INPUT_STYLE} required />
                        </>
                      )}

                      {/* --------------------------------------
                                        Database mode — shared vs dedicated.

                                        Shared (default) matches the current
                                        model: the tenant's data lives in the
                                        platform DB and is isolated by RLS.

                                        Dedicated triggers the provisioning
                                        saga in db_provisioner: a new Postgres
                                        database is created on the platform
                                        server, migrated + RLS-applied + seeded
                                        before the first admin invite fires.

                                        Disabled on Edit — switching modes on
                                        a live tenant requires a data
                                        migration (Phase 2).
                                    -------------------------------------- */}
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Database Mode
                          <span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <label
                            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                              dbMode === "shared" ? "border-button-secondary bg-button-secondary/5" : "border-border-primary"
                            } ${isEdit ? "opacity-60 cursor-not-allowed" : ""}`}
                          >
                            <Input
                              type="radio"
                              value="shared"
                              disabled={isEdit}
                              {...methods.register("dbMode")}
                              // A radio keeps the browser's own control, so the shared
                              // Input's text-field box is dropped here.
                              className="mt-1 w-auto appearance-auto rounded-none border-0 bg-transparent p-0"
                            />
                            <div>
                              <div className="text-sm font-medium text-text-primary">Shared</div>
                              <div className="text-xs text-text-primary/60 mt-0.5">
                                Data stays in the platform DB, isolated by RLS. Fast, cheap, unchanged from the current model.
                              </div>
                            </div>
                          </label>
                          <label
                            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                              dbMode === "dedicated" ? "border-button-secondary bg-button-secondary/5" : "border-border-primary"
                            } ${isEdit ? "opacity-60 cursor-not-allowed" : ""}`}
                          >
                            <Input
                              type="radio"
                              value="dedicated"
                              disabled={isEdit}
                              {...methods.register("dbMode")}
                              // A radio keeps the browser's own control, so the shared
                              // Input's text-field box is dropped here.
                              className="mt-1 w-auto appearance-auto rounded-none border-0 bg-transparent p-0"
                            />
                            <div>
                              <div className="text-sm font-medium text-text-primary">Dedicated</div>
                              <div className="text-xs text-text-primary/60 mt-0.5">
                                A brand-new Postgres database is provisioned on the platform server. Strongest isolation.
                              </div>
                            </div>
                          </label>
                        </div>
                        {dbMode === "dedicated" && !isEdit && (
                          <Callout className="mt-3 px-3 py-2 text-xs">
                            <div className="font-medium text-text-primary">Dedicated DB provisioning</div>
                            <div className="mt-0.5">
                              Creating this tenant will trigger the provisioning saga: CREATE DATABASE → migrations → RLS → seed → first-admin invite. It may take 15–30
                              seconds. If any step fails the operation is rolled back automatically.
                            </div>
                          </Callout>
                        )}
                      </div>

                      {/* --------------------------------------
                                        Modules — what this tenant's plan
                                        includes.

                                        Pre-filled with the platform defaults
                                        and reviewed on the next step before
                                        anything is written, because a wrong
                                        selection made here and corrected later
                                        is a wrong selection nobody notices.

                                        On Edit the tree is seeded from the
                                        tenant's own entitlements and saved with
                                        the rest of the form.
                                    -------------------------------------- */}
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-text-primary mb-2">Modules</label>
                        <ModuleSelector
                          modules={platformModules}
                          selected={selectedModules}
                          onToggle={handleModuleToggle}
                          loading={modulesLoading}
                          disabled={isSubmitting}
                          // CV Formatting is disabled — commented out, not deleted.
                          // renderExtra={(module, { checked }) =>
                          //   module.code === MODULES.RESUME_TEMPLATE ? (
                          //     <TemplateAccessPicker
                          //       templates={templateOptions}
                          //       selected={selectedTemplates}
                          //       onToggle={handleTemplateToggle}
                          //       locked={!checked}
                          //       loading={templatesLoading}
                          //       disabled={isSubmitting}
                          //     />
                          //   ) : null
                          // }
                        />
                      </div>

                      {/* --------------------------------------
                                        Prompt-safety policy.

                                        Decides who owns the tenant's guardian
                                        switch: the tenant, or the platform.
                                        A forced switch renders locked on the
                                        tenant side, with the reason.

                                        Shown on Add as well as Edit, and
                                        locked on both while the platform
                                        policy is a fleet-wide force.
                                    -------------------------------------- */}
                      {guardianLoaded && (
                        <div className="sm:col-span-2">
                          <label className="block text-sm font-medium text-text-primary mb-2">Prompt-safety policy</label>
                          <Select
                            value={platformForces ? platformGuardianPolicy : guardianPolicy}
                            onChange={setGuardianPolicy}
                            options={POLICY_OPTIONS}
                            disabled={isSubmitting || creating || platformForces}
                          />
                          {platformForces ? (
                            // The platform level wins. Leaving this control live
                            // said "Overrides the platform policy for this tenant",
                            // which was not true: a fleet-wide force is a floor,
                            // and the server now refuses a per-tenant write while
                            // one is in place. So the control is locked here too,
                            // and it says which setting is doing the deciding.
                            <p className="mt-1.5 text-xs text-text-primary/60">
                              Locked. The platform policy is <span className="font-medium text-text-primary">{GUARDIAN_POLICY_LABELS[platformGuardianPolicy]}</span> for
                              every tenant, and a per-tenant policy cannot override it{isEdit ? "" : ", so this tenant is created with it too"}. Change it on Platform
                              Settings, or set it back to "{GUARDIAN_POLICY_LABELS[GUARDIAN_POLICIES.TENANT_CHOICE]}" there to decide per tenant again.
                              {isEdit && storedGuardianPolicy !== GUARDIAN_POLICIES.TENANT_CHOICE && (
                                <> This tenant's stored policy — {GUARDIAN_POLICY_LABELS[storedGuardianPolicy]} — is kept and applies again from that moment.</>
                              )}
                            </p>
                          ) : (
                            <p className="mt-1.5 text-xs text-text-primary/60">
                              {guardianPolicy === GUARDIAN_POLICIES.TENANT_CHOICE
                                ? `No override — this tenant follows the platform policy (${GUARDIAN_POLICY_LABELS[platformGuardianPolicy]}), including any future change to it.`
                                : `Overrides the platform policy for this tenant while the platform leaves the choice open. Their own switch renders locked, with the reason.${isEdit ? "" : " It is applied as soon as the tenant is created."}`}
                            </p>
                          )}
                          {!guardianAvailable && (
                            <p className="mt-1.5 text-xs text-text-primary/60">
                              No guardian model is configured on this deployment, so no screening runs regardless of this setting.
                            </p>
                          )}
                          {!platformForces && guardianPolicy === GUARDIAN_POLICIES.FORCED_OFF && (
                            <Callout className="mt-2 px-3 py-2 text-xs">
                              <span className="font-medium text-text-primary">Screening stops for this tenant. They cannot switch it back on themselves.</span>
                            </Callout>
                          )}
                        </div>
                      )}

                      {/* Sourcing/Extension is disabled — commented out, not
                          deleted. See PLAN doc "resilient-stirring-tulip".
                      <div className="sm:col-span-2">
                        <RHFCheckboxGroup name="enabledExtensionSites" label="Enabled Browser Extension Sites" options={EXTENSION_SITE_OPTIONS} />
                        <p className="text-xs text-text-primary/50 mt-1">
                          Which sourcing sites this tenant's browser extension may be used with. Leave all unchecked to disable the extension entirely for this tenant.
                        </p>
                      </div>

                      <div className="sm:col-span-2">
                        <RHFCheckbox name="sourcingUsesOwnApiKeys" label="Tenant uses their own sourcing API keys" />
                        <p className="text-xs text-text-primary/50 mt-1">
                          When enabled, this tenant supplies its own API keys and the platform's keys are never used for them — any platform they have not configured will
                          be skipped when a campaign runs. They also need the "Credentials Manage Campaign" permission granted to a role before anyone can edit the keys.
                          Leave off to have sourcing run on the platform's keys.
                        </p>
                      </div>
                      */}

                      {/* --------------------------------------
                                        Compliance country masters (ATH-I1765).

                                        Editable on add AND edit: which countries
                                        a client recruits in changes as they
                                        grow.

                                        Ticking a country on edit seeds that
                                        master on save. UNticking one does not
                                        delete an already-seeded checklist — the
                                        tenant owns it from the moment it is
                                        written and may have edited it, so the
                                        copy note below says so rather than
                                        letting a SuperAdmin assume otherwise.
                                    -------------------------------------- */}
                      <div className="sm:col-span-2">
                        {/* Locked while Compliance is unticked — the sets are
                            compliance checklists, and the module is what gives
                            the tenant a screen to open one.

                            The ticks are NOT cleared by the lock. Greying a
                            control must never rewrite the state behind it: on
                            Edit that would revoke country sets the tenant has
                            spent months personalising, and re-ticking the
                            module would restore nothing. Same rule as
                            ModuleSelector's child lock and handleModuleToggle's
                            "deliberately does not touch". What Add sends when
                            the module is off is decided in confirmCreate. */}
                        <RHFCheckboxGroup
                          name="complianceCountryMasters"
                          label="Compliance Country Document Sets"
                          options={COUNTRY_MASTER_OPTIONS}
                          getOptionDisabled={() => !complianceSelected}
                          getOptionHint={() => (complianceSelected ? null : COUNTRY_MASTERS_NEED_COMPLIANCE)}
                        />
                        <p className="text-xs text-text-primary/50 mt-1">
                          Each country gives this tenant a ready-made checklist of the documents its compliance rules require, which their team copies from when building a checklist. Unchecking a country later leaves any checklist already created for it in place.
                        </p>
                        {!complianceSelected && (
                          <p className="text-xs text-tone-amber mt-1">
                            {isEdit
                              ? "Compliance is not enabled for this tenant, so nobody there can open a country document set. Any already seeded stay as they are."
                              : "Compliance is not selected, so no country document set will be seeded. Tick Compliance above to include them."}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 mt-6">
                      {/* Cancel */}
                      <DialogClose
                        render={
                          <Button variant="outline" className="bg-button-tertiary border-none" type="button">
                            Cancel
                          </Button>
                        }
                      />

                      {/* Submit */}
                      <Button className="bg-button-secondary text-white" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? "Saving…" : submitLabel}
                      </Button>
                    </div>
                  </div>
                </form>
              </FormProvider>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddEditModel;
